export type Token<T> = symbol & { readonly __type: T };

export function token<T>(name: string): Token<T> {
  return Symbol(name) as Token<T>;
}

export type Resolve = <T>(token: Token<T>) => T;
type Factory<T> = (resolve: Resolve) => T;
type Lifecycle = 'singleton' | 'transient';

interface Registration {
  factory: Factory<unknown>;
  lifecycle: Lifecycle;
}

const registrations = new Map<symbol, Registration>();
const instances = new Map<symbol, unknown>();
const resolving = new Set<symbol>();

export function registerSingleton<T>(t: Token<T>, factory: Factory<T>): void {
  registrations.set(t, { factory: factory as Factory<unknown>, lifecycle: 'singleton' });
}

export function registerTransient<T>(t: Token<T>, factory: Factory<T>): void {
  registrations.set(t, { factory: factory as Factory<unknown>, lifecycle: 'transient' });
}

export const resolve: Resolve = <T>(t: Token<T>): T => {
  const reg = registrations.get(t);
  if (!reg) throw new Error(`No registration for ${String(t)}`);

  if (reg.lifecycle === 'singleton' && instances.has(t)) {
    return instances.get(t) as T;
  }

  if (resolving.has(t)) {
    throw new Error(`Circular dependency detected resolving ${String(t)}`);
  }

  resolving.add(t);
  try {
    const instance = reg.factory(resolve) as T;
    if (reg.lifecycle === 'singleton') instances.set(t, instance);
    return instance;
  } finally {
    resolving.delete(t);
  }
};

export function reset(): void {
  registrations.clear();
  instances.clear();
  resolving.clear();
}
